import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { t, tr } from "../../i18n/translations";
import { toast } from "sonner";

export default function BookingDialog({ open, onClose, defaultService = null, courseId = null }) {
  const { lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: defaultService || "bridal",
    event_date: "",
    location: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      setForm((f) => ({
        ...f,
        service_type: courseId ? "course" : defaultService || "bridal",
      }));
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, defaultService, courseId]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, language: lang };
      if (courseId) payload.course_id = courseId;
      await api.post("/bookings", payload);
      toast.success(tr(t.booking.success, lang));
      setForm({ name: "", email: "", phone: "", service_type: "bridal", event_date: "", location: "", message: "" });
      onClose();
      setTimeout(() => {
        window.open("https://wa.me/message/SG2ELRTG32LVF1", "_blank", "noopener,noreferrer");
      }, 600);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const services = [
    ["bridal", t.booking.services.bridal],
    ["editorial", t.booking.services.editorial],
    ["events", t.booking.services.events],
    ["social", t.booking.services.social],
    ["course", t.booking.services.course],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="booking-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="booking-backdrop" />
      <div className="relative bg-[#FAFAFA] w-full sm:w-[560px] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#FAFAFA] border-b border-[#E8E1DA] px-8 py-5 flex items-center justify-between z-10">
          <div>
            <p className="overline text-[#8B6D5E]">Abril Suarez</p>
            <h2 className="font-serif text-2xl tracking-tight">{tr(t.booking.title, lang)}</h2>
          </div>
          <button onClick={onClose} data-testid="booking-close" className="text-[#1A1A1A] hover:text-[#8B6D5E]" aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="p-8 space-y-7" data-testid="booking-form">
          <p className="text-sm text-[#5C5C5C] leading-relaxed">{tr(t.booking.sub, lang)}</p>

          <div>
            <label className="field-label">{tr(t.booking.name, lang)}</label>
            <input
              required
              className="field"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="booking-name"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="field-label">{tr(t.booking.email, lang)}</label>
              <input
                required
                type="email"
                className="field"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="booking-email"
              />
            </div>
            <div>
              <label className="field-label">{tr(t.booking.phone, lang)}</label>
              <input
                required
                className="field"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="booking-phone"
              />
            </div>
          </div>
          <div>
            <label className="field-label">{tr(t.booking.service, lang)}</label>
            <select
              className="field"
              value={form.service_type}
              onChange={(e) => setForm({ ...form, service_type: e.target.value })}
              data-testid="booking-service"
              disabled={!!courseId}
            >
              {services.map(([k, label]) => (
                <option key={k} value={k}>
                  {tr(label, lang)}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="field-label">{tr(t.booking.date, lang)}</label>
              <input
                type="date"
                className="field"
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                data-testid="booking-date"
              />
            </div>
            <div>
              <label className="field-label">{tr(t.booking.location, lang)}</label>
              <input
                className="field"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Aguascalientes"
                data-testid="booking-location"
              />
            </div>
          </div>
          <div>
            <label className="field-label">{tr(t.booking.message, lang)}</label>
            <textarea
              className="field"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="booking-message"
            />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="booking-submit">
            {loading ? "…" : tr(t.booking.submit, lang)}
          </button>
        </form>
      </div>
    </div>
  );
}
